import React from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';

export enum ReportDateRange {
  YESTERDAY = 'yesterday',
  TODAY = 'today',
  TOMORROW = 'tomorrow',
  THIS_MONTH = 'thisMonth',
  LAST_MONTH = 'lastMonth',
}

/** In display order; dates are built on click so a tab left open overnight still means today. */
const DATE_RANGES: { type: ReportDateRange; dates: () => { from: Moment; to: Moment } }[] = [
  {
    type: ReportDateRange.YESTERDAY,
    dates: () => ({ from: moment().subtract(1, 'day'), to: moment().subtract(1, 'day') }),
  },
  { type: ReportDateRange.TODAY, dates: () => ({ from: moment(), to: moment() }) },
  { type: ReportDateRange.TOMORROW, dates: () => ({ from: moment().add(1, 'day'), to: moment().add(1, 'day') }) },
  { type: ReportDateRange.THIS_MONTH, dates: () => ({ from: moment().startOf('month'), to: moment().endOf('month') }) },
  {
    type: ReportDateRange.LAST_MONTH,
    dates: () => ({
      from: moment().subtract(1, 'month').startOf('month'),
      to: moment().subtract(1, 'month').endOf('month'),
    }),
  },
];

type Props = {
  fromDate: Moment;
  toDate: Moment;
  onChange: (from: Moment, to: Moment) => void;
  /** The pickers' own limits — a shortcut reaching past them is clamped rather than set to an invalid date. */
  minDate: Moment;
  maxDate: Moment;
};

export const ReportDateRangeShortcuts = ({ fromDate, toDate, onChange, minDate, maxDate }: Props) => {
  const { t } = useTranslation();

  const clampedRange = (range: (typeof DATE_RANGES)[number]) => {
    const { from, to } = range.dates();
    return {
      from: moment.max(moment.min(from, maxDate), minDate),
      to: moment.max(moment.min(to, maxDate), minDate),
    };
  };

  // Highlights the shortcut the pickers currently sit on, so editing a date visibly leaves the shortcuts.
  const selected =
    DATE_RANGES.find((range) => {
      const { from, to } = clampedRange(range);
      return from.isSame(fromDate, 'day') && to.isSame(toDate, 'day');
    })?.type ?? null;

  const handleChange = (_event: React.MouseEvent<HTMLElement>, type: ReportDateRange | null) => {
    const range = DATE_RANGES.find((candidate) => candidate.type === type);
    if (!range) {
      return;
    }
    const { from, to } = clampedRange(range);
    onChange(from, to);
  };

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={selected}
      onChange={handleChange}
      sx={{ flexWrap: 'wrap' }}
      aria-label={t('ordersReport.dateRange.label')}>
      {DATE_RANGES.map(({ type }) => (
        <ToggleButton key={type} value={type}>
          {t(`ordersReport.dateRange.${type}`)}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};
